import { Style } from 'ol/style';
import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Box, Checkbox, CircularProgress, Stack, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, TextField } from '@mui/material';

import { IntersectingLayer } from 'features/seismic-3d/models/types/IntersectingLayer';
import { useGetLayersIntersectingInGeom } from '../../projections/api/use3DFeatureInfoController';
import { FeatureGeom } from 'features/seismic-3d/models/classes/FeatureGeom';
import { GeopostCheckbox } from 'components/geopostCheckbox/GeopostCheckbox';
import { use3DViewerStore } from 'features/seismic-3d/stores/use3DViewerStore';
import { LayersList } from '../LayersList';
import { use3DSceneStore } from 'features/seismic-3d/stores/use3DSceneStore';
import { use3DGridStore } from 'features/seismic-3d/stores/use3DGridStore';
import { getThreeJsYFactor } from 'features/seismic-3d/utils/TileUtils';
import { LayersThreeJsFacade } from 'features/seismic-3d/threejs/facade/LayersThreeJsFacade';
import { LayersThreeJsContext } from '../LayersThreeJsContext';

type Props = {
    mainFeatureGeomWkt: string,
    mainFeatureGeomSrid: number
};

export function IntersectingLayersList({ mainFeatureGeomWkt, mainFeatureGeomSrid }: Props) {

    const [layerTitleToFilter, setLayerTitleToFilter] = useState<string>('');
    const { data: layersFound, mutate: refechIntersectingLayers, isLoading: isFoundLayersLoading } = useGetLayersIntersectingInGeom(mainFeatureGeomWkt, mainFeatureGeomSrid);
    const setSelectedLayerStore = use3DViewerStore(state => state.setSelectedLayers);
    const heightPixelFactor = use3DViewerStore(state => state.heightPixelFactor);
    const sampleInterval = use3DViewerStore(state => state.sampleInterval);
    const geopostScene = use3DSceneStore((state) => state.scene);
    const minProjectionHeight = useMemo(() => -(getThreeJsYFactor(heightPixelFactor, sampleInterval)), [heightPixelFactor, sampleInterval]);

    const [layerList, dispatch] = useReducer(layerListReducer, []);

    const filteredLayers = useMemo(() => {
        const regex = new RegExp(layerTitleToFilter, 'i');
        return layerList.filter((layer) => layerTitleToFilter === '' || regex.test(layer.Title));
    }, [layerList, layerTitleToFilter]);

    const layersThreeJsFacade = useMemo(() => !!geopostScene ? new LayersThreeJsFacade(geopostScene) : null, [geopostScene]);

    const mainFeatureCentroidX = use3DViewerStore(state => state.featureCentroidX);
    const mainFeatureCentroidY = use3DViewerStore(state => state.featureCentroidY);

    const removeSelectedLayer = useCallback((layer: IntersectingLayer) => {
        dispatch({ type: LayerListActionType.Uncheck, layer });
        geopostScene?.removeLayerMesh(layer.Id);
    }, [geopostScene]);

    const addSelectedLayer = useCallback((layer: IntersectingLayer, featuresGeomData: FeatureGeom[]) => {
        layersThreeJsFacade?.addLayerMesh(layer.Id, featuresGeomData, layer.FillColor, layer.FillOpacity, layer.StrokeColor, layer.ProjectionHeight ?? 0.01, [mainFeatureCentroidX, mainFeatureCentroidY]);
        dispatch({ type: LayerListActionType.Check, layer });
    }, [layersThreeJsFacade, mainFeatureCentroidX, mainFeatureCentroidY]);

    const updateSelectedLayer = useCallback((layer: IntersectingLayer) => {
        dispatch({ type: LayerListActionType.Update, layer });
    }, [layersThreeJsFacade]);

    const selectedLayers = useMemo(() => layerList.filter(layer => layer.Visible), [layerList]);

    useEffect(() => refechIntersectingLayers(), []);

    useEffect(() => {
        setSelectedLayerStore(selectedLayers);
    }, [selectedLayers]);

    useEffect(() => {
        if (!!layersFound) {
            dispatch({ type: LayerListActionType.Initialize, layers: layersFound });
        }
    }, [layersFound]);

    return (
        <Fragment>

            <Box
                component='form'
                noValidate sx={{
                    '& > :not(style)': { m: 1, width: '25ch' },
                }}
                autoComplete="off"
            >
                <Stack
                    direction='row'
                    spacing={2}
                >
                    <TextField
                        size='small'
                        id='title-input'
                        value={layerTitleToFilter}
                        onChange={(event) => { setLayerTitleToFilter(event.target.value) }}
                        label='Title'
                        variant='outlined' />
                    {
                        isFoundLayersLoading
                        &&
                        <CircularProgress size={25} />
                    }
                </Stack>
            </Box>
            {
                layersThreeJsFacade
                &&
                <LayersThreeJsContext.Provider value={layersThreeJsFacade}>
                    <LayersList
                        onAdd={addSelectedLayer}
                        onRemove={removeSelectedLayer}
                        onUpdate={updateSelectedLayer}
                        isCheckedCallback={(layer) => layer.Visible}
                        layers={filteredLayers}
                        minProjectionHeight={minProjectionHeight}
                    />
                </LayersThreeJsContext.Provider>
            }

        </Fragment>
    );
}

const layerListReducer = (layerList: IntersectingLayer[], action: LayerListAction) => {
    switch (action.type) {
        case LayerListActionType.Check:
            return layerList.map(layer => layer.Id === action.layer!.Id ? { ...layer, Visible: true } : layer);
        case LayerListActionType.Uncheck:
            return layerList.map(layer => layer.Id === action.layer!.Id ? { ...layer, Visible: false } : layer);
        case LayerListActionType.Update:
            return layerList.map(layer => layer.Id === action.layer!.Id ? action.layer : layer);
        case LayerListActionType.Initialize:
            return action.layers;
    }
};

enum LayerListActionType {
    Check = 0,
    Uncheck = 1,
    Update = 2,
    Initialize = 3,
}

type LayerListAction =
    | { type: LayerListActionType.Check; layer: IntersectingLayer }
    | { type: LayerListActionType.Uncheck; layer: IntersectingLayer }
    | { type: LayerListActionType.Update; layer: IntersectingLayer }
    | { type: LayerListActionType.Initialize; layers: IntersectingLayer[] };